<template>
  <!-- Full height from the first frame, so the page, the tabs and the browser's
       own scroll restore all see the whole menu; only the rows near the screen
       are drawn into it. -->
  <div
    ref="containerEl"
    :style="containerStyle"
  >
    <div class="lg:grid lg:grid-cols-2 lg:gap-x-10 2xl:grid-cols-3 2xl:gap-x-14">
      <template
        v-for="item in drawnItems"
        :key="item.id"
      >
        <slot :item="item" />
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup generic="T extends { id: string | number }">
import { useElementBounding, useMediaQuery, useResizeObserver, useWindowSize } from '@vueuse/core'
import { computed, ref } from 'vue'

/**
 * One section's dishes, drawn only near the screen.
 *
 * Every row has the same height (`--pj-menu-row`, `--pj-menu-line`), so row n
 * sits at n × height and nothing is measured per row. The section element stays
 * in the page at its full height, which keeps the tabs, the headings and
 * anchors working as they did when every dish was drawn.
 */
const props = defineProps<{
  items: readonly T[]
  /** One row's height in px, border included. */
  rowHeight: number
}>()

defineSlots<{ default: (props: { item: T }) => unknown }>()

/** Rows drawn beyond each edge of the screen, in screens, so a fast swipe never shows a gap. */
const OVERSCAN_SCREENS = 1

const containerEl = ref<HTMLElement | null>(null)

const { top, update: measure } = useElementBounding(containerEl)

// Content above can move this grid without resizing it or scrolling the window, as when the server's skeleton goes.
useResizeObserver(document.body, measure)

const { height: screenHeight } = useWindowSize()

// Matches the lg and 2xl columns in the template.
const isTwoColumns = useMediaQuery('(min-width: 1024px)')
const isThreeColumns = useMediaQuery('(min-width: 1536px)')

const columns = computed(() => {
  if (isThreeColumns.value) return 3
  if (isTwoColumns.value) return 2

  return 1
})

const rowCount = computed(() => Math.ceil(props.items.length / columns.value))

const clampRow = (row: number): number => Math.min(Math.max(row, 0), rowCount.value)

const firstRow = computed(() =>
  clampRow(Math.floor((-top.value - screenHeight.value * OVERSCAN_SCREENS) / props.rowHeight))
)

const lastRow = computed(() =>
  clampRow(Math.ceil((screenHeight.value * (1 + OVERSCAN_SCREENS) - top.value) / props.rowHeight))
)

const drawnItems = computed(() => props.items.slice(firstRow.value * columns.value, lastRow.value * columns.value))

const containerStyle = computed(() => ({
  height: `${rowCount.value * props.rowHeight}px`,
  paddingTop: `${firstRow.value * props.rowHeight}px`
}))
</script>
